import { Link, usePage } from '@inertiajs/react';
import { useState, useEffect } from 'react';
import { BlogLayout } from './BlogLayout';
import { TableOfContents, MobileTableOfContents, processContentWithIds, processContentWithIdsMobile } from '@/components/blog/TableOfContents';
import { useMediaQuery } from '@/lib/utils';

// Types 
interface Post {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  content_html?: string;
  coverImageUrl: string | null;
  publishedAt: string | null;
  readingTimeMinutes: number;
  viewCount: number;
  visibility: string; // 'visible' or 'auth_only'
  status: string;
  author: {
    id: number;
    name: string;
    avatarUrl: string | null;
  } | null;
  categories: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  tags: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
}


interface PageProps extends Record<string, unknown> {
  post?: Post;
  relatedPosts?: Post[];
  recentPosts?: Post[];
  auth?: {
    user?: {
      id: number;
      email: string;
      name: string;
      username: string;
      role: string;
    };
  };
}

// Helper function to format date
function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Premium Badge Component
function PremiumBadge() {
  return (
    <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium px-2 py-1 rounded-full">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      Premium
    </span>
  );
}

// Simple markdown to HTML converter for blog content
function markdownToHtml(markdown: string): string {
  if (!markdown) return '';
  
  let html = markdown;
  
  // Headers
  html = html.replace(/^### (.*)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*)$/gm, '<h1>$1</h1>');
  
  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Italic
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Code blocks
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code class="language-$1">$2</code></pre>');
  
  // Inline code
  html = html.replace(/`(.*?)`/g, '<code class="bg-muted text-foreground px-1 py-0.5 rounded text-sm">$1</code>');
  
  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline">$1</a>');
  
  // Unordered lists
  html = html.replace(/^\- (.*)$/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ul class="list-disc pl-6 my-4">$&</ul>');
  
  // Ordered lists
  html = html.replace(/^\d+\. (.*)$/gm, '<li>$1</li>');
  
  // Paragraphs
  html = html.replace(/\n\n/g, '</p><p class="my-4">');
  html = '<p class="my-4">' + html + '</p>';
  
  // Line breaks
  html = html.replace(/\n/g, '<br>');
  
  return html;
}

// Share Button Component
function ShareButton({ post }: { post: Post }) {
  const [shareUrl, setShareUrl] = useState('');
  
  useEffect(() => {
    setShareUrl(window.location.href);
  }, []);
  
  const encodedTitle = encodeURIComponent(post.title);
  const encodedUrl = encodeURIComponent(shareUrl);

  const shareOptions = [
    {
      name: 'Twitter',
      url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
    {
      name: 'LinkedIn',
      url: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      ),
    },
    {
      name: 'Copy Link',
      url: '#',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      onClick: () => {
        navigator.clipboard.writeText(shareUrl);
      }
    },
  ];

  return (
    <div className="flex items-center space-x-3">
      <span className="text-sm text-muted-foreground">Share:</span>
      {shareOptions.map((option) => (
        option.onClick ? (
          <button
            key={option.name}
            onClick={option.onClick}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors"
            title={option.name}
          >
            {option.icon}
          </button>
        ) : (
          <a
            key={option.name}
            href={option.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors"
            title={option.name}
          >
            {option.icon}
          </a>
        )
      ))}
    </div>
  );
}

// Related Post Card
function RelatedPostCard({ post }: { post: Post }) {
  return (
    <article className="bg-card dark:bg-card text-foreground rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow">
      {post.coverImageUrl && (
        <Link href={`/blog/${post.slug}`} className="block">
          <div className="aspect-video relative overflow-hidden">
            <img 
              src={post.coverImageUrl} 
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </Link>
      )}
      <div className="p-4">
        <Link href={`/blog/${post.slug}`}>
          <h3 className="font-semibold text-foreground mb-1 hover:text-primary transition-colors line-clamp-2">
            {post.title}
          </h3>
        </Link>
        <p className="text-xs text-muted-foreground">
          {formatDate(post.publishedAt)} • {post.readingTimeMinutes} min read
        </p>
      </div>
    </article>
  );
}

// Main Show Page
export default function BlogShow() {
  const props = usePage<PageProps>().props;
  const post = props.post;
  const relatedPosts = props.relatedPosts || [];
  const recentPosts = props.recentPosts || [];

  // Detect mobile viewport - use mobile scroll margins below lg breakpoint (1024px)
  const isMobileViewport = useMediaQuery('(max-width: 1023px)');

  // For SSR, handle missing post gracefully
  if (!post) {
    return (
      <BlogLayout>
        <div className="max-w-5xl mx-auto py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </BlogLayout>
    );
  }

  // For SSR, check visibility instead of isPremium
  const isPremium = post.visibility === 'auth_only';

  // Use SSR content directly - no need for separate body fetch
  // The page is SSR rendered with full content, and /api/me handles auth check
  const contentHtml = post.content_html || post.content;

  // Process content with appropriate scroll margins based on viewport
  // Note: On SSR, isMobileViewport defaults to false, so we use desktop margins initially
  // On client, this will re-run when isMobileViewport changes
  const processedContent = isMobileViewport 
    ? processContentWithIdsMobile(contentHtml) 
    : processContentWithIds(contentHtml);

  // Create the Table of Contents component for left sidebar
  const tableOfContents = (
    <TableOfContents content={contentHtml} minHeadings={2} />
  );

  return (
    <BlogLayout leftSidebar={tableOfContents}>
      <article className="max-w-4xl mx-auto">
        {/* Mobile TOC - inline at top of content */}
        <MobileTableOfContents content={contentHtml} minHeadings={2} />
        {/* Header */}
        <header className="mb-8">
          {/* Categories */}
          {(post.categories?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {isPremium && (
                <PremiumBadge />
              )}
              {post.categories.map((category) => (
                <Link
                  key={category.id}
                  href={`/blog/category/${category.slug}`}
                  className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full hover:bg-primary/20 transition-colors"
                >
                  {category.name}
                </Link>
              ))}
            </div>
          )}
          
          {/* Title */}
          <h1 className="text-4xl font-bold text-foreground mb-4">
            {post.title}
          </h1>
          
          {/* Summary */}
          {post.summary && (
            <p className="text-xl text-muted-foreground mb-6">
              {post.summary}
            </p>
          )}
          
          {/* Meta */}
          <div className="flex flex-wrap items-center justify-between gap-4 py-4 border-y border-border">
            <div className="flex items-center space-x-4">
              {/* Author */}
              {post.author && (
                <div className="flex items-center space-x-2">
                  {post.author.avatarUrl ? (
                    <img 
                      src={post.author.avatarUrl} 
                      alt={post.author.name}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-medium">
                        {post.author.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-foreground">{post.author.name}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(post.publishedAt)}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Reading time & views */}
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {post.readingTimeMinutes} min read
              </span>
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {post.viewCount} views
              </span>
            </div>
          </div>
          
          {/* Share */}
          <div className="mt-4">
            <ShareButton post={post} />
          </div>
        </header>
        

        
        {/* Blog Content */}
        <div id="blog-content"
          className="prose prose-lg dark:prose-invert max-w-none mb-12 text-foreground"
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />
        
        {/* Tags */}
        {(post.tags?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2 mb-8 py-6 border-t border-b border-border">
            {post.tags.map((tag) => (
              <Link
                key={tag.id}
                href={`/blog/tag/${tag.slug}`}
                className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-full hover:bg-muted/80 transition-colors"
              >
                #{tag.name}
              </Link>
            ))}
          </div>
        )}
        
        {/* Related Posts */}
        {(relatedPosts?.length ?? 0) > 0 && (
          <section className="mt-12 pt-12 border-t border-border">
            <h2 className="text-xl font-bold text-foreground mb-4">Related Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedPosts.map((relatedPost) => (
                <RelatedPostCard key={relatedPost.id} post={relatedPost} />
              ))}
            </div>
          </section>
        )}
      </article>
    </BlogLayout>
  );
}
